import { Timestamp } from 'firebase/firestore';
import { format, formatDistanceToNow, differenceInDays, differenceInHours } from 'date-fns';
import type { WaterStatus } from '../types/plant';
import { getSmartWaterStatus } from './wateringSchedule';

/**
 * Calculate water status based on days since last watered.
 * When effectiveInterval is provided, uses smart plant-aware thresholds.
 * Otherwise falls back to legacy hardcoded 2/5 day thresholds.
 */
export function getWaterStatus(lastWatered: Timestamp | null, effectiveInterval?: number | null): WaterStatus {
  if (effectiveInterval != null && effectiveInterval > 0) {
    return getSmartWaterStatus(lastWatered, effectiveInterval);
  }

  if (!lastWatered) {
    return 'never-watered';
  }

  const daysSinceWatered = differenceInDays(new Date(), lastWatered.toDate());

  if (daysSinceWatered <= 2) {
    return 'fresh';
  }
  if (daysSinceWatered <= 5) {
    return 'due-soon';
  }
  return 'overdue';
}

/**
 * Get color class for water status.
 */
export function getWaterStatusColor(status: WaterStatus): string {
  switch (status) {
    case 'fresh':
      return 'text-success';
    case 'due-soon':
      return 'text-warning';
    case 'overdue':
      return 'text-danger';
    case 'never-watered':
      return 'text-gray-500';
    default:
      return 'text-gray-500';
  }
}

/**
 * Get background color class for water status indicator.
 */
export function getWaterStatusBgColor(status: WaterStatus): string {
  switch (status) {
    case 'fresh':
      return 'bg-success';
    case 'due-soon':
      return 'bg-warning';
    case 'overdue':
      return 'bg-danger';
    case 'never-watered':
      return 'bg-gray-400';
    default:
      return 'bg-gray-400';
  }
}

/**
 * Get human-readable water status text.
 * When effectiveInterval is provided, shows forward-looking text.
 */
export function getWaterStatusText(lastWatered: Timestamp | null, effectiveInterval?: number | null): string {
  if (!lastWatered) {
    return 'Never watered';
  }

  const daysSinceWatered = differenceInDays(new Date(), lastWatered.toDate());

  // Forward-looking text when interval is known
  if (effectiveInterval != null && effectiveInterval > 0) {
    const daysUntil = effectiveInterval - daysSinceWatered;
    if (daysSinceWatered === 0) {
      const hoursSince = differenceInHours(new Date(), lastWatered.toDate());
      return hoursSince === 0 ? 'Just watered' : `Watered ${hoursSince}h ago`;
    }
    if (daysUntil > 1) return `Water in ${daysUntil} days`;
    if (daysUntil === 1) return 'Water tomorrow';
    if (daysUntil === 0) return 'Water today';
    if (daysUntil === -1) return 'Overdue by 1 day';
    return `Overdue by ${Math.abs(daysUntil)} days`;
  }

  // Legacy backward-looking text
  if (daysSinceWatered === 0) {
    const hoursSince = differenceInHours(new Date(), lastWatered.toDate());
    if (hoursSince === 0) {
      return 'Just watered';
    }
    return `Watered ${hoursSince}h ago`;
  }

  if (daysSinceWatered === 1) {
    return 'Watered yesterday';
  }

  return `Watered ${daysSinceWatered} days ago`;
}

/**
 * Format a timestamp to a short date string.
 */
export function formatDate(timestamp: Timestamp | null, fallback = 'Not set'): string {
  if (!timestamp) return fallback;
  return format(timestamp.toDate(), 'MMM d, yyyy');
}

/**
 * Format a timestamp to a relative time string (e.g., "2 days ago").
 */
export function formatRelativeTime(timestamp: Timestamp | null): string {
  if (!timestamp) return 'Never';
  return formatDistanceToNow(timestamp.toDate(), { addSuffix: true });
}

/**
 * Format milliseconds to a human-readable countdown (e.g., "5 min 30 sec").
 */
export function formatCountdown(ms: number): string {
  if (ms <= 0) return 'now';

  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) {
    return `${seconds} sec`;
  }
  if (seconds === 0) {
    return `${minutes} min`;
  }
  return `${minutes} min ${seconds} sec`;
}

/**
 * Pluralize a word based on count.
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural || `${singular}s`);
}
